import { logEvent, setUserProperties } from 'firebase/analytics';
import { getAnalyticsInstance } from '../firebase';

/**
 * Analytics Utility for BCM Anesthesia App
 * Tracks user engagement, page views, and key interactions
 */

// Helper to safely log events (handles cases where analytics might not be initialized)
const safeLogEvent = (eventName, params = {}) => {
  const analytics = getAnalyticsInstance();
  if (analytics) {
    try {
      logEvent(analytics, eventName, params);
      console.log(`📊 Analytics: ${eventName}`, params);
    } catch (error) {
      console.warn('Analytics event failed:', error);
    }
  }
};

/**
 * Page View Tracking
 */
export const trackPageView = (pageName, additionalParams = {}) => {
  safeLogEvent('page_view', {
    page_name: pageName,
    page_title: document.title,
    ...additionalParams
  });
};

/**
 * Hospital Views
 */
export const trackHospitalView = (hospitalId, hospitalName) => {
  safeLogEvent('hospital_view', {
    hospital_id: hospitalId,
    hospital_name: hospitalName
  });
};

/**
 * Education Content Tracking
 */
export const trackEducationView = (contentType, contentTitle, contentId = null) => {
  safeLogEvent('education_content_view', {
    content_type: contentType,
    content_title: contentTitle,
    content_id: contentId
  });
};

export const trackVideoPlay = (videoTitle, videoUrl) => {
  safeLogEvent('video_play', {
    video_title: videoTitle,
    video_url: videoUrl
  });
};

export const trackPDFView = (pdfTitle, pdfUrl) => {
  safeLogEvent('pdf_view', {
    pdf_title: pdfTitle,
    pdf_url: pdfUrl
  });
};

export const trackPresentationView = (presentationTitle, presentationUrl) => {
  safeLogEvent('presentation_view', {
    presentation_title: presentationTitle,
    presentation_url: presentationUrl
  });
};

/**
 * File Downloads
 */
export const trackFileDownload = (fileName, fileType, fileUrl) => {
  safeLogEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    file_url: fileUrl
  });
};

/**
 * Protocol Tracking
 */
export const trackProtocolView = (protocolName, protocolCategory) => {
  safeLogEvent('protocol_view', {
    protocol_name: protocolName,
    protocol_category: protocolCategory
  });
};

/**
 * Search Tracking
 */
export const trackSearch = (searchQuery, resultsCount) => {
  safeLogEvent('search', {
    search_term: searchQuery,
    results_count: resultsCount
  });
};

/**
 * BCM Department Sections
 */
export const trackBCMSectionView = (sectionName) => {
  safeLogEvent('bcm_section_view', {
    section_name: sectionName
  });
};

/**
 * Research Hub Tracking
 */
export const trackResearchView = (researchSection) => {
  safeLogEvent('research_view', {
    research_section: researchSection
  });
};

/**
 * User Actions
 */
export const trackFeedbackSubmission = (feedbackType) => {
  safeLogEvent('feedback_submitted', {
    feedback_type: feedbackType
  });
};

export const trackNeedlestickView = (hospitalId) => {
  safeLogEvent('needlestick_protocol_view', {
    hospital_id: hospitalId
  });
};

export const trackDarkModeToggle = (enabled) => {
  safeLogEvent('dark_mode_toggle', {
    enabled: enabled
  });
};

/**
 * App Engagement
 */
export const trackAppInstall = () => {
  safeLogEvent('app_install');
};

export const trackAppUpdate = (fromVersion, toVersion) => {
  safeLogEvent('app_update', {
    from_version: fromVersion,
    to_version: toVersion
  });
};

/**
 * Set User Properties
 */
export const setAnalyticsUserProperty = (propertyName, value) => {
  const analytics = getAnalyticsInstance();
  if (analytics) {
    try {
      setUserProperties(analytics, { [propertyName]: value });
      console.log(`📊 User property set: ${propertyName} = ${value}`);
    } catch (error) {
      console.warn('Failed to set user property:', error);
    }
  }
};

/**
 * Track engagement time on specific pages/content
 */
export const trackEngagementTime = (contentType, contentId, durationSeconds) => {
  safeLogEvent('content_engagement', {
    content_type: contentType,
    content_id: contentId,
    engagement_duration: durationSeconds
  });
};

/**
 * Clinical Tools Tracking
 */
export const trackClinicalToolView = (toolName) => {
  safeLogEvent('clinical_tool_view', {
    tool_name: toolName
  });
};

export const trackMedicationReference = (medicationType) => {
  safeLogEvent('medication_reference_view', {
    medication_type: medicationType
  });
};
